import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckSquare, Plus, PencilSimple, Trash, MagnifyingGlass, ArrowUp, ArrowDown, ArrowsDownUp, Export } from "@phosphor-icons/react";
import { useTableFeatures, exportCSV } from "@/hooks/useTableFeatures";

const defaultForm = {
  client_id: "", client_name: "", frn: "", funding_year: "",
  service_provider: "", spin: "", service_start_date: "", status: "pending", notes: "",
};
const statusColors = {
  pending: "bg-[#FFD100]/10 text-[#946B00] border-[#FFD100]/20",
  approved: "bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20",
  rejected: "bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00]/20",
};

export default function Form486() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...defaultForm });

  const { search, setSearch, sortKey, sortDir, toggleSort, filters, setFilter, clearFilters, processed, activeFilterCount } = useTableFeatures(items);
  const csvCols = [
    { label: "Client", key: "client_name" }, { label: "FRN", key: "frn" }, { label: "FY", key: "funding_year" },
    { label: "Provider", key: "service_provider" }, { label: "SPIN", key: "spin" },
    { label: "Service Start", key: "service_start_date" }, { label: "Status", key: "status" },
  ];
  const SH = ({ k, children }) => (
    <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold cursor-pointer select-none hover:text-[#1A1D20]" onClick={() => toggleSort(k)}>
      <div className="flex items-center gap-1">{children}{sortKey === k ? (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowsDownUp size={10} className="opacity-30" />}</div>
    </TableHead>
  );

  useEffect(() => { load(); loadClients(); }, []);
  const load = async () => { try { const { data } = await api.get("/forms/486"); setItems(data); } catch { toast.error("Failed to load"); } };
  const loadClients = async () => { try { const { data } = await api.get("/clients"); setClients(data); } catch {} };

  const save = async () => {
    try {
      if (editing) { await api.put(`/forms/486/${editing}`, form); toast.success("Updated"); }
      else { await api.post("/forms/486", form); toast.success("Created"); }
      setOpen(false); load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => { if (!window.confirm("Delete?")) return; try { await api.delete(`/forms/486/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };
  const edit = (item) => { setForm({ ...item }); setEditing(item.id); setOpen(true); };
  const openNew = () => { setForm({ ...defaultForm }); setEditing(null); setOpen(true); };
  const filtered = items.filter((i) => JSON.stringify(i).toLowerCase().includes(search.toLowerCase()));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectClient = (id) => { const c = clients.find((cl) => cl.id === id); setForm((f) => ({ ...f, client_id: id, client_name: c ? c.name : "" })); };

  return (
    <div className="p-6" data-testid="form486-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">E-Rate Forms</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>FCC Form 486</h1>
          <p className="text-sm text-[#6B7280] mt-1">Receipt of Service Confirmation</p>
        </div>
        <button data-testid="add-form486-btn" onClick={openNew} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm flex items-center gap-2 transition-colors">
          <Plus size={16} weight="bold" /> Add Form 486
        </button>
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input data-testid="search-form486" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none pl-9" />
        </div>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilter("status", v)}>
          <SelectTrigger className="rounded-none w-[120px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="rounded-none"><SelectItem value="all">All</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent>
        </Select>
        <Select value={filters.funding_year || "all"} onValueChange={(v) => setFilter("funding_year", v)}>
          <SelectTrigger data-testid="filter-486-year" className="rounded-none w-[140px]"><SelectValue placeholder="All Years" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">All Years</SelectItem>
            {[2026, 2027, 2028, 2029, 2030].map((y) => <SelectItem key={y} value={String(y)}>FY {y}</SelectItem>)}
          </SelectContent>
        </Select>
        {activeFilterCount > 0 && <button onClick={clearFilters} className="text-xs text-[#002FA7] hover:underline">Clear</button>}
        <button data-testid="export-form486-btn" onClick={() => exportCSV(processed, csvCols, "form486.csv")} className="ml-auto flex items-center gap-1.5 bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-3 py-2 text-xs transition-colors">
          <Export size={14} /> Export CSV
        </button>
      </div>

      <div className="border border-gray-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="data-table-header">
              <SH k="client_name">Client</SH>
              <SH k="frn">FRN</SH>
              <SH k="funding_year">FY</SH>
              <SH k="service_provider">Provider</SH>
              <SH k="spin">SPIN</SH>
              <SH k="service_start_date">Service Start</SH>
              <SH k="status">Status</SH>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {processed.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#6B7280] text-sm">No Form 486 records</TableCell></TableRow>
            ) : processed.map((item) => (
              <TableRow key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                <TableCell className="py-3 px-4 text-[#1A1D20] font-medium">{item.client_name || "—"}</TableCell>
                <TableCell className="py-3 px-4 font-mono text-xs text-[#002FA7]">{item.frn || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm">{item.funding_year || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm">{item.service_provider || "—"}</TableCell>
                <TableCell className="py-3 px-4 font-mono text-xs">{item.spin || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm">{item.service_start_date || "—"}</TableCell>
                <TableCell className="py-3 px-4"><Badge className={`rounded-none text-xs border capitalize ${statusColors[item.status] || statusColors.pending}`}>{item.status}</Badge></TableCell>
                <TableCell className="py-3 px-4">
                  <div className="flex items-center gap-1">
                    <button data-testid={`edit-form486-${item.id}`} onClick={() => edit(item)} className="p-1.5 hover:bg-gray-100"><PencilSimple size={14} className="text-[#6B7280]" /></button>
                    <button data-testid={`delete-form486-${item.id}`} onClick={() => remove(item.id)} className="p-1.5 hover:bg-red-50"><Trash size={14} className="text-[#FF2A00]" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-[560px]">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{editing ? "Edit" : "Add"} Form 486</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Client</label>
              <Select value={form.client_id} onValueChange={selectClient}>
                <SelectTrigger data-testid="form486-client-select" className="rounded-none"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent className="rounded-none max-h-[200px]">{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">FRN</label>
                <Input data-testid="form486-frn-input" value={form.frn} onChange={(e) => set("frn", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Funding Year</label>
                <Input data-testid="form486-fy-input" value={form.funding_year} onChange={(e) => set("funding_year", e.target.value)} className="rounded-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Service Provider</label>
                <Input data-testid="form486-provider-input" value={form.service_provider} onChange={(e) => set("service_provider", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">SPIN</label>
                <Input data-testid="form486-spin-input" value={form.spin} onChange={(e) => set("spin", e.target.value)} className="rounded-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Service Start Date</label>
                <Input data-testid="form486-start-input" type="date" value={form.service_start_date} onChange={(e) => set("service_start_date", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Status</label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="form486-status-select" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none"><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Notes</label>
              <textarea data-testid="form486-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full border border-gray-300 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] min-h-[60px]" />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="cancel-form486-btn" onClick={() => setOpen(false)} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm">Cancel</button>
            <button data-testid="save-form486-btn" onClick={save} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
